import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Box, Button, Typography } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts/highstock';
import { FunctionComponent, useState } from 'react';
import { useAppTranslate } from '../../../../core/hooks';
import { ChartSettings } from '../../../chart-settings/model';
import { BitRateMetric, buildSeriesData, NetworkServiceBitrateViewModel, ThroughputMonitoredServiceApiModel } from '../../model';
import { BitrateStatisticsTableChartDialog } from '../BitrateStatisticsTableChartDialog/BitrateStatisticsTableChartDialog';
import { SeriesConfiguration, useTimeSeriesStatisticsHighchartsOptions } from '../../../timeseries-statistics/hooks/TimeSeriesStatistics.hook';
import { formatThroughput } from '../../../../l3-services/model';

interface Props {
  chartData: NetworkServiceBitrateViewModel;
  monitoredService: ThroughputMonitoredServiceApiModel;
  metrics: BitRateMetric[];
  unit: string;
  chartSettings: ChartSettings;
  customerId: number | null;
}

export const buildNamedSeriesConfiguration = (
  chartData: NetworkServiceBitrateViewModel,
  metric: BitRateMetric,
  seriesName: string
): SeriesConfiguration => ({
  seriesName,
  seriesData: buildSeriesData(chartData, metric)
    .map(point => ({ ...point, name: seriesName }))
});

export const BitrateStatisticsTableChart: FunctionComponent<Props> = ({
  monitoredService,
  chartData,
  metrics,
  unit,
  chartSettings,
  customerId,
}) => {
  const translate = useAppTranslate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const isBitRateChart = metrics.includes("txBitRate");
  const chartTitle = 'tableHeaders.' + (isBitRateChart ? "bitRateChartTitle" : "trafficChartTitle");
  const seriesConifgurations = metrics
    .map(metric => buildNamedSeriesConfiguration(chartData, metric, translate('tableHeaders.' + metric)));
  const tooltipPointValueFormatter = isBitRateChart ? formatThroughput : undefined
  const highchartsOptions = useTimeSeriesStatisticsHighchartsOptions(chartSettings, unit, seriesConifgurations, tooltipPointValueFormatter);

  return (
    <Box role={'presentation'} sx={{ background: '#fff', borderRadius: '1rem', padding: 1 }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 3
      }}>
        <Typography>{ `${translate(chartTitle)} [${unit}]` }</Typography>
        <Button
          variant='slim'
          size='small'
          endIcon={ <FullscreenIcon /> }
          onClick={ () => setDialogOpen(true) }
        >
          { translate('charts.fullview') }
        </Button>
      </Box>
      <HighchartsReact highcharts={ Highcharts } options={ highchartsOptions } />
      <BitrateStatisticsTableChartDialog
        chartTitle={ chartTitle }
        initialChartSettings={{ resolution: chartSettings.resolution, timespan: chartSettings.timespan }}
        initialChartData={ chartData }
        monitoredService={ monitoredService }
        metrics={ metrics }
        unit={ unit }
        open={ dialogOpen }
        onClose={ () => setDialogOpen(false) }
        customerId={ customerId }
        tooltipPointValueFormatter={ tooltipPointValueFormatter }
      />
    </Box>
  );
};